<template>
  <Spinner v-if="!billingEntitlementsReady && !$store.getters.isSuperAdmin"/>

  <BillingFeatureNotice
      v-else-if="!canAccessSmsSenderNames"
      feature="visit_sms_reminder"
      variant="page"
      :title="$t('SMS Sender Names')"
      :description="$t('smsSettingsDescription')"/>

  <template v-else>
    <Toolbar class="p-mb-4 p-mt-4 p-p-2">
      <template #start>
        <div style="font-size: 1.4em; color: inherit" class="p-p-2">
          {{ $t('SMS Sender Names') }}
        </div>
        <div class="p-p-1" v-if="$store.getters.isOwnerOrHigher">
          <AddNewButton
              :disabled="tenantDataIsLoading || hasPendingName || (items?.length >= 5 && $store.getters.effectiveRole !== constants.userRoles.superAdmin)"
              :loading="tenantDataIsLoading"
              :label="$t('Add')"
              @click="addNewItem"/>
        </div>
      </template>
    </Toolbar>

    <Spinner v-if="isLoading"/>
    <DesktopDataTable
        v-else
        :dataTableIsLoading="dataTableIsLoading"
        :showHeader="false"
        :itemsArrayIsEmpty="itemsArrayIsEmpty"
        :sort="sort"
        :showSearchInput="false"
        :actionButtonsCount="actionButtonsCount"
        @sort-items="sortItems"
        :headers="[
          {name: 'name', title: $t('Sender Name'), sortable: false},
          {name: 'is_active', title: $t('active'), sortable: false},
          {name: 'status', title: $t('Status'), sortable: false},
          {name: 'created_at', title: $t('Created'), sortable: false},
        ]">
      <template #body>
        <tr v-for="(item, index) of items"
            :key="item.id ?? index"
            :class="{ 'updated-item': isItemUpdated(item.id) }">
          <td>
            <span v-if="item.name">{{ item.name }}</span>
            <i v-else class="ti-minus"/>
          </td>
          <td>
            <i class="pi pi-check green-color" v-if="item.status === 1"/>
            <i class="pi pi-times warning-color" v-else/>
          </td>
          <td>
            <SmsSenderStatus :state="item?.state != null ? Number(item.state) : null"/>
          </td>
          <td>
            <span v-if="item.created_at">
              <template v-if="$store.getters.isSuperAdmin">
                {{ formatBy(item.created_at, item.createdBy) }}
              </template>
              <template v-else>
                {{ formatDate(item.created_at) }}
              </template>
            </span>
            <i v-else class="ti-minus"/>
          </td>
        </tr>
      </template>
    </DesktopDataTable>

    <OwnPaginator
        v-show="items && items.length"
        :rows="pagination.perPage"
        :currentPage="pagination.currentPage - 1"
        :totalPages="pagination.pageCount"
        :totalRecords="pagination.totalCount"
        :rowsPerPageOptions="[5, 10, 25, 50, 100, 200]"
        :pageLinkSize="11"/>

    <TotalRecordsData
        :pagination="pagination"
        :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

    <SmsSenderNameModal
        :visible="itemModal"
        :item="item"
        @update-items="updateItems"
        @close="closeItemModal"/>
  </template>
</template>

<script>
import httpClient from '@/services/http.services'
import datatableMixins from '@/mixins/datatableMixins'
import settings from '@/settings'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import formatMixins from '@/mixins/formatMixins'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import SmsSenderNameModal from '@/pages/account/components/SmsSenderNameModal'
import SmsSenderStatus from '@/pages/account/components/SmsSenderStatus'
import BillingFeatureNotice from '@/components/billing/BillingFeatureNotice.vue'
import constants from '@/constants'

export default {
  mixins: [
    datatableMixins,
    showErrorsMixins,
    formatMixins,
    billingFeaturesMixins,
  ],
  components: {
    SmsSenderNameModal,
    SmsSenderStatus,
    BillingFeatureNotice,
  },
  props: {
    tenantDataIsLoading: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')
      if (
          this.$route.path === '/login' ||
          route.indexOf('account') === -1 ||
          route.indexOf('sms-sender-names') === -1 ||
          !this.canAccessSmsSenderNames
      ) {
        return false
      }

      this.checkRouteQuery()
      this.getItems()
    },
    billingEntitlementsReady(ready) {
      if (ready && this.canAccessSmsSenderNames) {
        this.getPageData()
      }
    },
  },
  data() {
    return {
      fieldsExpand: 'createdBy',
      apiLink: 'sms-sender',
      routePath: '/sms-sender-names',
      sort: {
        column: 'id',
        direction: '-',
      },
      constants,
    }
  },
  mounted() {
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) {
      return false
    }

    this.checkRouteQuery()
    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    getPageData() {
      this.getItems()
    },
    async getItems() {
      if (
          !this.canAccessSmsSenderNames ||
          !this.userCanGetData ||
          this.fetchInFlight
      ) {
        return
      }

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('smsSenderNames')

      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']

          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch (err) {
        this.items = []
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.dataTableIsLoading = false
        this.isLoading = false

        if (ctrl && this._abortors?.smsSenderNames === ctrl) {
          this._abortors.smsSenderNames = null
        }
      }
    },
  },
  computed: {
    canAccessSmsSenderNames() {
      return this.$store.getters.isSuperAdmin || this.canUseVisitSmsReminder
    },
    hasPendingName() {
      return this.items?.some(item => Number(item.state) === 1)
    },
    actionButtonsCount() {
      return 0
    },
  },
}
</script>

<style scoped lang="scss">
td {
  height: 44px;
}
</style>